import React, { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent, ChangeEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import {
  OtpContainer,
  OtpInputBox,
  OtpInputsWrapper,
  ErrorText,
} from "./styles";

export interface KLOtpInputProps {
  /**
   * Number of OTP digits (default: 6)
   */
  length?: number;
  /**
   * Callback when OTP is complete
   */
  onComplete: (otp: string) => void;
  /**
   * Callback when OTP value changes
   */
  onChange?: (otp: string) => void;
  /**
   * Disable all inputs
   */
  disabled?: boolean;
  /**
   * Error message to display
   */
  error?: string | null;
  /**
   * Auto-submit when complete
   */
  autoSubmit?: boolean;
  /**
   * Auto-focus first input on mount (default: true)
   */
  autoFocus?: boolean;
}

const KLOtpInput: React.FC<KLOtpInputProps> = ({
  length = 6,
  onComplete,
  onChange,
  disabled = false,
  error = null,
  autoSubmit = false,
  autoFocus = true,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const reducedMotion = useReducedMotion();

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Focus first input on mount if autoFocus is enabled
  useEffect(() => {
    if (autoFocus && !disabled && inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  }, [disabled, autoFocus]);

  // Handle OTP completion
  useEffect(() => {
    const otpString = otp.join("");

    // Notify parent of changes
    onChange?.(otpString);

    // Check if complete - if joined string length matches expected length, all boxes are filled
    if (otpString.length === length) {
      onComplete(otpString);

      // Auto-submit: blur the last input to trigger form submission feel
      if (autoSubmit && inputRefs.current[length - 1]) {
        inputRefs.current[length - 1]?.blur();
      }
    }
  }, [otp, length, onComplete, onChange, autoSubmit]);

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-advance to next input if value entered
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        // If current box is empty, go back and clear previous
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current box
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
      e.preventDefault();
    }

    // Handle arrow keys - boxes are LTR so navigation follows LTR conventions
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      if (e.key === "ArrowRight" && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    // Handle Delete key
    if (e.key === "Delete") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      e.preventDefault();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // Only process if pasted data contains only digits
    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    // Take only the required length
    const digits = pastedData.slice(0, length).split("");
    const newOtp = [...otp];

    digits.forEach((digit, index) => {
      if (index < length) {
        newOtp[index] = digit;
      }
    });

    setOtp(newOtp);

    // Focus the last filled box or the first empty one
    const lastFilledIndex = Math.min(digits.length - 1, length - 1);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleFocus = (index: number) => {
    // Select the text when focused for easier editing
    inputRefs.current[index]?.select();
  };

  return (
    <OtpContainer>
      <OtpInputsWrapper>
        {otp.map((digit, index) => (
          <OtpInputBox
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="\d{1}"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            $hasValue={!!digit}
            $error={!!error}
            aria-label={`ספרה ${index + 1} מתוך ${length}`}
            autoComplete={index === 0 ? "one-time-code" : "off"}
          />
        ))}
      </OtpInputsWrapper>

      <AnimatePresence mode="wait">
        {error && (
          <ErrorText
            role="alert"
            aria-live="polite"
            as={motion.div}
            initial={reducedMotion ? {} : { opacity: 0, y: -10 }}
            animate={reducedMotion ? {} : { opacity: 1, y: 0 }}
            exit={reducedMotion ? {} : { opacity: 0, y: -10 }}
            transition={reducedMotion ? { duration: 0 } : { duration: 0.2 }}
          >
            {error}
          </ErrorText>
        )}
      </AnimatePresence>
    </OtpContainer>
  );
};

export default KLOtpInput;
